import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CopyResourceResultDc } from "@evergis/api";

import { Notification } from "../../../../ducks/notifications/notifications";

type NotificationPayload = {
  alias: string;
  tableAlias?: string;
  jobResult?: CopyResourceResultDc[];
};

export type CopyJobNotification = {
  successNotification?: Notification;
  partialSuccessNotification?: Notification;
  errorNotification?: Notification;
};

export enum JobSource {
  Projects = "projects",
  Layers = "layers",
  Tables = "tables",
  BulkProjects = "bulkProjects",
  BulkLayers = "bulkLayers",
  BulkTables = "bulkTables",
}

const getResourcesCount = (resource: string, jobResult: CopyResourceResultDc[]) =>
  (jobResult && jobResult.filter(({ type }) => type === resource).length) || 0;

export const useCopyJobNotifications = (jobSource: JobSource, payload?: NotificationPayload) => {
  const { t } = useTranslation("dialogs");

  const partialSuccessNotification = useMemo(
    () => ({
      warning: true,
      title: t("copyResource.notAllResourcesCopied"),
    }),
    [t],
  );

  const errorNotification = useMemo(
    () => ({
      error: true,
      title: t("copyResource.copyError"),
    }),
    [t],
  );

  const defaultSucceededNotification = useMemo(
    () => ({
      success: true,
      title: t("copyResource.copySuccess"),
    }),
    [t],
  );

  const getProjectsInfo = useCallback(
    ({ alias, jobResult }: NotificationPayload) => {
      const projectsCount = getResourcesCount("project", jobResult);
      const layersCount = getResourcesCount("layer", jobResult);
      const tablesCount = getResourcesCount("table", jobResult);
      const isMultiCopy = projectsCount > 1;

      const layersDescription = layersCount ? `, ${t("copyResource.layersCopied")}: ${layersCount}` : "";
      const tablesDescription = tablesCount ? `, ${t("copyResource.ofTables")}: ${tablesCount}` : "";

      const description = isMultiCopy
        ? `${alias} ${t("copyResource.and")} ${projectsCount - 1}`
        : `${alias}${layersDescription}${tablesDescription}`;

      return {
        success: true,
        title: isMultiCopy ? t("copyResource.projectsCopiedSuccessfully") : t("copyResource.projectCopiedSuccessfully"),
        description,
      };
    },
    [t],
  );

  const getLayersInfo = useCallback(
    ({ alias, tableAlias, jobResult }: NotificationPayload) => {
      const layersCount = getResourcesCount("layer", jobResult);
      const isMultiCopy = layersCount > 1;

      const tableInfo = tableAlias ? `, ${t("copyResource.table2")}: ${tableAlias}` : "";
      const description = isMultiCopy ? `${alias} ${t("copyResource.and")} ${layersCount - 1}` : `${alias}${tableInfo}`;

      return {
        title: isMultiCopy ? t("copyResource.layersCopiedSuccessfully") : t("copyResource.layerCopiedSuccessfully"),
        description,
      };
    },
    [t],
  );

  const getTablesInfo = useCallback(
    ({ alias, jobResult }: NotificationPayload) => {
      const tablesCount = getResourcesCount("table", jobResult);
      const isMultiCopy = tablesCount > 1;
      const description = isMultiCopy ? `${alias} ${t("copyResource.and")} ${tablesCount - 1}` : `${alias}`;

      return {
        success: true,
        title: isMultiCopy ? t("copyResource.tablesCopiedSuccessfully") : t("copyResource.tableCopiedSuccessfully"),
        description,
      };
    },
    [t],
  );

  const successNotification = useMemo(() => {
    switch (jobSource) {
      case JobSource.Projects:
      case JobSource.BulkProjects:
        return getProjectsInfo(payload);

      case JobSource.Layers:
      case JobSource.BulkLayers:
        return getLayersInfo(payload);

      case JobSource.Tables:
      case JobSource.BulkTables:
        return getTablesInfo(payload);

      default:
        return defaultSucceededNotification;
    }
  }, [defaultSucceededNotification, getLayersInfo, getProjectsInfo, getTablesInfo, jobSource, payload]);

  return {
    successNotification,
    partialSuccessNotification,
    errorNotification,
  };
};
